/**
 * Development Metrics under a Concept — sibling to Units (ConceptUnitsSection.jsx), shared by
 * the Reference > Design > Concepts branch (DesignSection.jsx) and the DAPC Drawings milestone
 * Design node (MilestoneSections.jsx). All fields live directly on the Project Concept record
 * (tblaAwLsCi6XGsdFY) — no separate table/fetch; reads straight from ctx.conceptCache (already
 * loaded by the time this renders, since it's always a sibling of an already-rendered concept row)
 * and writes via services/design.js#updateConcept.
 *
 * Field split, per Airtable's own field types:
 *   - 'number' fields (Proposed Lot Coverage Area, Proposed Open Space, Proposed Landscaped Open
 *     Space, the 7 setbacks, the 2 building heights, the 2 parking counts) are user-entered —
 *     each has its own ⋯ "Edit" action opening a single-field modal (MetricFieldEditModal). No
 *     batch/category-level edit action — every field is edited independently, on purpose.
 *   - 'formula' fields (Lot Coverage Percent, Open Space Percent, Landscaped Open Space Percent,
 *     every "Conforms" flag) are computed by Airtable — display only.
 *   - 'multipleLookupValues' fields (Required Covered/Uncovered Parking Spaces) are read-only
 *     reference values pulled from Property Data.
 *
 * Lot Coverage Area is ALWAYS manual (2026-07 decision): Airtable also has a
 * 'Proposed Lot Coverage Area Calculated' rollup-based field (summed from each level's Area Plan
 * Metrics that has 'Include in Lot Coverage' checked — see ConceptLevelAreaFields.jsx) and a
 * 'Proposed Lot Coverage Area' formula that would prefer the Override when set, else fall back to
 * that Calculated value. This section deliberately reads/writes 'Proposed Lot Coverage Area
 * Override' directly instead — the calculated/computed path is never shown here, since lot
 * coverage is jurisdiction-defined and doesn't reliably map to a straight sum of level areas
 * (some level configurations don't count the way the automatic sum assumes). 'Proposed Lot
 * Coverage Percent' and 'Lot Coverage Conforms' still chain off the Airtable formula field, which
 * picks up whatever's in Override once it's set.
 *
 * Renders in two modes — tree (default, full header) vs zoomMode (children only, used for
 * sec:concept:<conceptId>:metrics). No category-level menu action in either mode.
 */
import { useState, useEffect, useReducer } from 'react'
import { createPortal } from 'react-dom'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { getFirst } from '../outline/utils.js'
import { useActionMenu, ActionMenu, ActionMenuButton, ActionMenuItem } from './ActionMenu.jsx'
import { updateConcept } from '../services/design.js'

/* ── Field config — screenshot order, each editable field paired with its Conforms flag(s) ── */
const METRIC_FIELDS = [
  { label: 'Proposed Lot Coverage Area',    field: 'Proposed Lot Coverage Area Override', kind: 'sf',  editable: true },
  { label: 'Proposed Lot Coverage Percent', field: 'Proposed Lot Coverage Percent', kind: 'pct', computed: true, conformsFields: ['Lot Coverage Conforms'] },
  { label: 'Proposed Open Space',           field: 'Proposed Open Space',           kind: 'sf',  editable: true },
  { label: 'Proposed Open Space Percent',   field: 'Proposed Open Space Percent',   kind: 'pct', computed: true, conformsFields: ['Open Space Conforms'] },
  { label: 'Proposed Landscaped Open Space',         field: 'Proposed Landscaped Open Space',         kind: 'sf',  editable: true },
  { label: 'Proposed Landscaped Open Space Percent', field: 'Proposed Landscaped Open Space Percent', kind: 'pct', computed: true, conformsFields: ['Landscaped Open Space Conforms'] },
  { label: 'Proposed Front Yard Setback',      field: 'Proposed Front Yard Setback',      kind: 'ft', editable: true, conformsFields: ['Front Yard Conforms'] },
  { label: 'Proposed Rear Yard Setback',       field: 'Proposed Rear Yard Setback',       kind: 'ft', editable: true, conformsFields: ['Rear Yard Conforms'] },
  { label: 'Proposed Side Yard Setback 1',     field: 'Proposed Side Yard Setback 1',     kind: 'ft', editable: true, conformsFields: ['Side Yard 1 Conforms'] },
  { label: 'Proposed Side Yard Setback 2',     field: 'Proposed Side Yard Setback 2',     kind: 'ft', editable: true, conformsFields: ['Side Yard 2 Conforms'] },
  { label: 'Proposed ADU Rear Yard Setback',   field: 'Proposed ADU Rear Yard Setback',   kind: 'ft', editable: true, conformsFields: ['ADU Rear Yard Conforms'] },
  { label: 'Proposed ADU Side Yard Setback 1', field: 'Proposed ADU Side Yard Setback 1', kind: 'ft', editable: true, conformsFields: ['ADU Side Yard 1 Conforms'] },
  { label: 'Proposed ADU Side Yard Setback 2', field: 'Proposed ADU Side Yard Setback 2', kind: 'ft', editable: true, conformsFields: ['ADU Side Yard 2 Conforms'] },
  { label: 'Proposed Building Height from Grade FF',    field: 'Proposed Building Height from Grade FF',    kind: 'ft', editable: true, conformsFields: ['Proposed Height from Grade FF Conforms'] },
  { label: 'Proposed Building Height from Slope Datum', field: 'Proposed Building Height from Slope Datum', kind: 'ft', editable: true, conformsFields: ['Proposed Height from Front PL Conforms', 'Proposed Height from Rear PL Conforms'] },
  { label: 'Required Covered Parking Spaces',   field: 'Required Covered Parking Spaces (from Property Data) (from Project)',   kind: 'count', reference: true },
  { label: 'Proposed Covered Parking Spaces',   field: 'Proposed Covered Parking Spaces',   kind: 'count', editable: true, conformsFields: ['Covered Parking Conforms'] },
  { label: 'Required Uncovered Parking Spaces', field: 'Required Uncovered Parking Spaces (from Property Data) (from Project)', kind: 'count', reference: true },
  { label: 'Proposed Uncovered Parking Spaces', field: 'Proposed Uncovered Parking Spaces', kind: 'count', editable: true, conformsFields: ['Uncovered Parking Conforms'] },
]

// Airtable returns formula results as an object rather than a number/string when the formula
// hits an error or indeterminate case (typically a divide-by-zero from blank input fields) —
// e.g. {specialValue: 'NaN'} for a percentage formula, {error: '#ERROR!'} for a field that
// concatenates another errored field. Treat both shapes as blank rather than falling through to
// String(v), which renders the literal text "[object Object]" (see ConceptFieldCategorySection.jsx,
// where this was confirmed live on Demolition's percentage fields with blank input areas).
function isAirtableFormulaError(v) { return !!v && typeof v === 'object' && !Array.isArray(v) && ('error' in v || 'specialValue' in v) }

function isBlank(v) { return v === null || v === undefined || v === '' || isAirtableFormulaError(v) }

/** Count of "No" Conforms flags among METRIC_FIELDS only (not every *Conforms field on the
 *  record — e.g. Landscaped Open Space Conforms isn't part of this node's field set). Shared by
 *  the category node's own meta and ZoomTitle's trailing meta so the two numbers never disagree. */
export function countNotConforming(concept) {
  if (!concept) return 0
  return METRIC_FIELDS.reduce((sum, f) => sum + (f.conformsFields || []).filter(cf => concept.fields[cf] === 'No').length, 0)
}

function fmtValue(raw, kind) {
  const v = getFirst(raw)
  if (isBlank(v)) return '—'
  const n = typeof v === 'number' ? v : parseFloat(v)
  if (Number.isNaN(n)) return String(v)
  if (kind === 'pct')   return (n * 100).toFixed(2) + '%'
  if (kind === 'sf')    return n.toFixed(2) + ' SF'
  if (kind === 'ft')    return n.toFixed(2) + ' ft'
  if (kind === 'count') return String(Math.round(n))
  return String(n)
}

function ConformsPill({ value }) {
  if (isBlank(value)) return null
  const ok = value === 'Yes'
  const c = ok ? '#1D9E75' : '#c0392b'
  return (
    <span className="wf-status-pill" style={{ color: c, borderColor: c + '33' }}>
      {ok ? '✓ Conforms' : '✗ Does not conform'}
    </span>
  )
}

function FieldOriginTag({ text }) {
  return (
    <span style={{
      fontFamily: '"DM Sans",sans-serif', fontSize: 9, fontStyle: 'italic', textTransform: 'uppercase',
      letterSpacing: '0.04em', color: 'var(--aj-text-muted)', marginLeft: 6,
    }}>
      {text}
    </span>
  )
}

function MetricRow({ f, fields, onEdit, dataKey }) {
  const value = fields[f.field]
  const empty = isBlank(getFirst(value))
  const conformsValues = f.conformsFields || []
  const menu = useActionMenu()
  return (
    <div className="wf-node wf-node--item" data-key={dataKey}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden" />
        <span className="wf-bullet wf-bullet--item" style={{ background: 'var(--wf-dot)' }} />
        <div className="wf-text-wrap">
          <span className="wf-node-text">{f.label}</span>
          <span style={{
            fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8,
            color: empty ? 'var(--aj-text-muted)' : 'var(--aj-text-mid)',
          }}>
            {fmtValue(value, f.kind)}
          </span>
          {f.computed && <FieldOriginTag text="Computed" />}
          {f.reference && <FieldOriginTag text="Reference" />}
          {!empty && conformsValues.length > 0 && (
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {conformsValues.map(cf => <ConformsPill key={cf} value={fields[cf]} />)}
            </span>
          )}
        </div>
        {f.editable && <ActionMenuButton menu={menu} />}
      </div>
      {f.editable && (
        <ActionMenu menu={menu}>
          <ActionMenuItem onClick={() => { menu.close(); onEdit(f) }}>Edit</ActionMenuItem>
        </ActionMenu>
      )}
    </div>
  )
}

/* ── MetricFieldEditModal — single-field edit form ───────────────────────────────────────────── */
function MetricFieldEditModal({ field, concept, onSave, onClose }) {
  const [value, setValue] = useState(() => concept.fields[field.field] ?? '')
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    try {
      await onSave(field.field, value === '' ? null : parseFloat(value))
      onClose()
    } catch {
      setSaving(false)
    }
  }

  const inp = { fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '6px 8px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-subtle)', color: 'var(--aj-text)', outline: 'none', width: '100%', boxSizing: 'border-box' }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: '28px 32px', width: 400, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 16 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--aj-text)' }}>
          Edit {field.label}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 600, color: 'var(--aj-text-mid)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {field.kind === 'sf' ? 'Square feet' : field.kind === 'ft' ? 'Feet' : field.kind === 'count' ? 'Count' : ''}
          </div>
          <input autoFocus type="number" step="any" value={value} onChange={e => setValue(e.target.value)} style={inp} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8, borderTop: '0.5px solid var(--aj-border)', marginTop: 4 }}>
          <button onClick={onClose} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'var(--aj-text)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '7px 16px', border: 'none', borderRadius: 4, background: '#07272D', color: '#fff', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default function ConceptDevMetricsSection({ conceptId, zoomMode }) {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  const [editingField, setEditingField] = useState(null)
  const key = 'sec:concept:' + conceptId + ':metrics'
  const isCollapsed = zoomMode ? false : (wf.collapsed[key] !== false)

  // Re-render when the concept cache is populated/updated (e.g. Units finishes its own load,
  // or a save here updates ctx.conceptCache) — this component has no fetch of its own.
  useEffect(() => {
    function onRender() { forceUpdate() }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  const concept = (ctx.conceptCache || []).find(c => c.id === conceptId)

  async function handleSave(fieldName, value) {
    const updated = await updateConcept(conceptId, { [fieldName]: value })
    if (Array.isArray(ctx.conceptCache)) {
      const idx = ctx.conceptCache.findIndex(c => c.id === conceptId)
      if (idx !== -1) ctx.conceptCache[idx] = { ...ctx.conceptCache[idx], fields: { ...ctx.conceptCache[idx].fields, ...updated.fields } }
    }
    ctx.render()
  }

  const notConformingCount = countNotConforming(concept)

  const content = !concept ? (
    <div className="wf-loading">Loading…</div>
  ) : (
    <>
      {METRIC_FIELDS.map(f => <MetricRow key={f.field} dataKey={'metric:' + conceptId + ':' + f.field} f={f} fields={concept.fields} onEdit={setEditingField} />)}
    </>
  )

  const editModal = editingField && concept && (
    <MetricFieldEditModal field={editingField} concept={concept} onSave={handleSave} onClose={() => setEditingField(null)} />
  )

  if (zoomMode) {
    return (
      <div className="wf-list">
        {content}
        {editModal}
      </div>
    )
  }

  return (
    <div className={`wf-node wf-node--cat${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`} />
        </button>
        <button className="wf-bullet wf-bullet--cat" title="Focus on Development Metrics"
          data-fn="zoomTo" data-level="concept-metrics" data-conceptid={conceptId} />
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--cat">Development Metrics</span>
          {notConformingCount > 0 && (
            <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, marginLeft: 8, color: '#c0392b' }}>
              {notConformingCount} not conforming
            </span>
          )}
        </div>
      </div>
      {!isCollapsed && <div className="wf-children">{content}</div>}
      {editModal}
    </div>
  )
}
